import { treaty } from '@elysiajs/eden'
import * as Sentry from '@sentry/react'
import type { App } from '../../../backend/src/index'
import { createClient } from '../supabase/client'

// Get the API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL!

// Create Supabase client instance - handles ALL auth (storage, refresh, everything)
const supabase = createClient()

// Export supabase for direct use throughout the app
export { supabase }

// Create Eden Treaty client with custom fetcher that adds auth headers
// and automatically retries on 401 with refreshed token
export const api = treaty<App>(API_URL, {
  fetcher: async (url: RequestInfo | URL, init?: RequestInit) => {
    // Get current session from Supabase
    // Use getSession() first (fast, reads from localStorage)
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // Add Authorization header if we have a session
    const headers = new Headers(init?.headers)
    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`)
    }

    // Make the request with the updated headers
    let response = await fetch(url, {
      ...init,
      headers,
    })

    // If 401 and we had a session, try refreshing and retry once
    if (response.status === 401 && session?.access_token) {
      console.log('[API] Got 401, refreshing session and retrying')
      const {
        data: { session: newSession },
        error,
      } = await supabase.auth.refreshSession()

      if (!error && newSession?.access_token) {
        // Update Authorization header with new token
        headers.set('Authorization', `Bearer ${newSession.access_token}`)

        // Retry the request
        response = await fetch(url, {
          ...init,
          headers,
        })
      }
    }

    return response
  },
})

/**
 * Get stored user data from Supabase with validation (async)
 * Uses getUser() which validates the token is still valid
 */
export async function getStoredUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser()
  if (error) {
    console.warn('[getStoredUser] Auth validation failed:', error.message)
    return null
  }
  return user
}

/**
 * Fetch wrapper with automatic auth headers from Supabase session
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers = new Headers(options.headers)
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`)
  }

  return fetch(url, {
    ...options,
    headers,
  })
}

/**
 * Wrapper for API calls with Sentry error tracking
 */
export async function apiCall<T>(
  fn: () => Promise<T>,
  context: { endpoint: string; method: string; userId?: string }
): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    // Capture API errors in Sentry
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        tags: {
          api_endpoint: context.endpoint,
          api_method: context.method,
          api_error: 'true',
        },
        contexts: {
          api: {
            endpoint: context.endpoint,
            method: context.method,
          },
        },
        ...(context.userId && {
          user: { id: context.userId },
        }),
      })
    }
    throw error
  }
}

// Legacy exports for backward compatibility - these just trigger auth state changes
export function storeTokens() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-changed'))
  }
}

export async function clearTokens() {
  await supabase.auth.signOut()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-changed'))
  }
}
